/**
 * Onboarding System Types
 * Types for Driver.js-based interactive tutorials and onboarding flow
 */

import type { DriveStep, Config } from 'driver.js';

/** Unique identifiers for each tour */
export type TourId =
  | 'quick-start'
  | 'research'
  | 'representatives'
  | 'alerts'
  | 'committee'
  | 'bill-details';

/** Unique identifiers for checklist items */
export type ChecklistItemId =
  | 'track-bill'
  | 'understand-bill'
  | 'explore-legislator'
  | 'search-bill'
  | 'set-alert'
  | 'explore-committee';

/** Persisted onboarding state stored in localStorage */
export interface OnboardingState {
  /** Array of completed tour IDs */
  completedTours: TourId[];
  /** Progress on checklist items */
  checklistProgress: Record<ChecklistItemId, boolean>;
  /** Whether user has permanently dismissed onboarding UI */
  dismissed: boolean;
  /** Whether welcome modal has been shown */
  welcomeShown: boolean;
  /** ISO timestamp of last interaction */
  lastSeenAt: string;
}

/** Single item in the onboarding checklist */
export interface ChecklistItem {
  id: ChecklistItemId;
  label: string;
  description: string;
  /** Tour to launch when user clicks this item */
  tourId?: TourId;
  /** Route to navigate to for this action */
  route?: string;
}

/** Tour definition with metadata and steps */
export interface Tour {
  id: TourId;
  name: string;
  description: string;
  /** Driver.js steps */
  steps: DriveStep[];
  /** Optional route to navigate to before starting tour */
  startRoute?: string;
}

/** Configuration options for useDriver hook */
export interface UseDriverOptions extends Omit<Config, 'steps'> {
  steps: DriveStep[];
  onComplete?: () => void;
  onSkip?: () => void;
}

/** Return type of useDriver hook */
export interface UseDriverReturn {
  start: (stepIndex?: number) => void;
  stop: () => void;
  next: () => void;
  prev: () => void;
  moveTo: (index: number) => void;
  isActive: () => boolean;
  hasNextStep: () => boolean;
  hasPreviousStep: () => boolean;
}

/** Context value for OnboardingProvider */
export interface OnboardingContextValue {
  /** Current onboarding state */
  state: OnboardingState;
  /** Whether onboarding UI should be visible */
  isVisible: boolean;
  /** Whether a tour is currently running */
  isTourActive: boolean;
  /** Currently active tour ID, if any */
  activeTourId: TourId | null;
  /** Start a specific tour */
  startTour: (tourId: TourId) => void;
  /** Mark a tour as completed */
  completeTour: (tourId: TourId) => void;
  /** Mark a checklist item as completed */
  completeChecklistItem: (itemId: ChecklistItemId) => void;
  /** Mark welcome modal as shown */
  markWelcomeShown: () => void;
  /** Dismiss onboarding UI permanently */
  dismissOnboarding: () => void;
  /** Reset all onboarding progress */
  resetOnboarding: () => void;
  /** Calculate completion percentage */
  getCompletionPercentage: () => number;
}

/** Default initial state for onboarding */
export const INITIAL_ONBOARDING_STATE: OnboardingState = {
  completedTours: [],
  checklistProgress: {
    'track-bill': false,
    'understand-bill': false,
    'explore-legislator': false,
    'search-bill': false,
    'set-alert': false,
    'explore-committee': false,
  },
  dismissed: false,
  welcomeShown: false,
  lastSeenAt: new Date().toISOString(),
};

/** Checklist items configuration - ordered by user value */
export const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    id: 'track-bill',
    label: 'Track your first bill',
    description: 'Find and follow legislation you care about',
    tourId: 'quick-start',
    route: '/bills',
  },
  {
    id: 'understand-bill',
    label: 'Understand a bill',
    description: 'Learn what each section tells you',
    tourId: 'bill-details',
    route: '/bills/HB2438',
  },
  {
    id: 'explore-legislator',
    label: 'Find your representative',
    description: 'See who represents your district',
    tourId: 'representatives',
    route: '/legislators',
  },
  {
    id: 'search-bill',
    label: 'Use semantic search',
    description: 'Find bills by concept using AI',
    tourId: 'research',
    route: '/search',
  },
  {
    id: 'set-alert',
    label: 'Configure notifications',
    description: 'Choose how you receive bill updates',
    tourId: 'alerts',
    route: '/account',
  },
  {
    id: 'explore-committee',
    label: 'Browse committees',
    description: 'See how bills move through the process',
    tourId: 'committee',
    route: '/committees',
  },
];
